/**
 * Which locales the BACKEND actually holds.
 *
 * Terminology is a chained lookup — localStorage cache, then the API, then the
 * `en-standard` map bundled into this build. When the API has no terminology at
 * all, the third link answers every read and the second one cannot do anything
 * but 404. It still costs a request per namespace, and there are six of them on
 * every page load:
 *
 *   GET /api/terms/profiles                  → {"profiles":[]}
 *   GET /api/terms/en-standard/cgt           → 404 Unknown locale: en-standard
 *   GET /api/terms/en-standard/subject       → 404   (…and four more)
 *
 * That is seven of the thirteen API calls a page load makes on such an
 * environment, spent to learn nothing, against a limiter that allows 120 a
 * minute per caller. It is what tipped e2e run 33619861345 into 429s, and the
 * app does not retry a 429 by design (see QueryProvider) — so the refusals it
 * caused landed on `/auth/me` and `/tenants`, and the page rendered empty.
 *
 * So the profile list, which is fetched anyway to build the fallback graph, also
 * decides whether the HTTP link is worth trying:
 *
 *   | Profile list says          | HTTP read |
 *   |----------------------------|-----------|
 *   | never resolved (not asked, | attempted — an unanswered question is not
 *   |  offline, refused)         |   an answer; behave exactly as before      |
 *   | resolved, has the locale   | attempted |
 *   | resolved, lacks the locale | skipped — the server already said no       |
 *
 * The distinction in the first row is the point: only a list the backend
 * actually returned closes the gate. A transient failure leaves terminology
 * working the way it does today rather than silently pinning the app to bundled
 * terms.
 */

/** `null` until the backend's profile list has resolved at least once. */
let known: Set<string> | null = null

/**
 * Record the locale ids the backend reported. Called from `applyLocales`, so
 * adding or removing a language reopens the gate without a reload.
 */
export function setServerLocales(ids: Iterable<string>): void {
  known = new Set(ids)
}

/** Back to the unresolved state — the boot condition, and each test's start. */
export function resetServerLocales(): void {
  known = null
}

/**
 * Is a terms request for this locale worth sending? True while the profile list
 * is unknown, so this can only ever remove requests that were going to 404.
 */
export function canServerHaveLocale(locale: string): boolean {
  return known === null || known.has(locale)
}
